import type { Message } from '../../types';
import { readMessageTiaValidation } from '../tiaValidationMetadata';

/**
 * Which toolchain a turn was for, read from a field that is often not there.
 *
 * `pipeline.requirement.brand` is the only brand a rendered turn carries, and a
 * turn reloaded from the server carries no `pipeline` at all. Every surface that
 * needs the answer therefore needs a default, and on 2026-09-11 two of them
 * disagreed: the file extension read the missing value as non-Siemens and
 * answered `.st`, while the block splitter's else-branch answered Siemens — one
 * turn delivered as `..._V1.st` beside `ProgramBlocks/PRG_Main.scl`.
 *
 * So the question is asked once, here, and answered the way the product's own
 * default answers it (`getSavedPlcBrand` → CODESYS): Siemens only when something
 * says Siemens.
 */
export function isSiemensBrand(brand?: string): boolean {
  const value = String(brand || '').toLowerCase();
  return value.includes('siemens') || value.includes('tia') || value === 's7';
}

export function isBeckhoffBrand(brand?: string): boolean {
  const value = String(brand || '').toLowerCase();
  return value.includes('beckhoff') || value.includes('twincat');
}

/** True for a stated CODESYS brand and for an unstated one. */
export function isCodesysBrand(brand?: string): boolean {
  return !isSiemensBrand(brand) && !isBeckhoffBrand(brand);
}

/** The compiler a turn's program is handed to: TIA Portal's or CODESYS's. */
export type NativeCompileConnector = 'tia' | 'codesys';

/**
 * Brands RealPLC has no native compiler for, so their turns have no such step.
 *
 * Inovance and ABB are not among them: this module reads them as CODESYS, as it
 * reads an unstated brand. The runtime's own `isCodesysTarget` does not, so the
 * compile of such a turn ends up reported as never run (待核对).
 */
function hasNoNativeCompiler(brand?: string): boolean {
  const value = String(brand || '').toLowerCase();
  return isBeckhoffBrand(value)
    || value.includes('allen-bradley')
    || value.includes('rockwell')
    || value.includes('mitsubishi')
    || value.includes('omron');
}

/**
 * Which native compiler, if any, this turn's program went to.
 *
 * A TIA record answers first. The gate writes one on the Siemens turns that
 * reach it and marks any other target it sees `not_siemens_target`, and the
 * record stays on a turn reloaded from the server, which has no pipeline and so
 * no brand. Without a record the brand decides, read as everywhere in this
 * module: Siemens only when something says Siemens, CODESYS when nothing does.
 * The brand the turn recorded on its metrics survives a reload too.
 *
 * 2026-09-23 线上（TIA-UI-019）：Siemens 回合结束后，执行记录第 8 行仍是
 * 「原生编译（CODESYS）· 等待中」——TIA 因目标项目未打开一次都没编译，这一行
 * 却只认得 CODESYS。
 */
export function nativeCompileConnector(
  message: Pick<Message, 'tiaValidation' | 'pipeline' | 'generationMetrics'>,
): NativeCompileConnector | null {
  const tiaValidation = readMessageTiaValidation(message);
  if (tiaValidation && tiaValidation.reason !== 'not_siemens_target') return 'tia';
  const brand = message.pipeline?.requirement?.brand || message.generationMetrics?.targetBrand;
  if (isSiemensBrand(brand)) return 'tia';
  if (hasNoNativeCompiler(brand)) return null;
  return 'codesys';
}
